<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import HeroBanner from "../components/HeroBanner.svelte";
	import aboutHeader from "../assets/videos/brano-about-us-header.mp4";
	import aboutPoster from "../assets/videos/brano-about-us-header.jpg?webp&w=1920&imagetools";
	import aboutHeaderMobile from "../assets/videos/brano-about-us-header-mobile.mp4";
	import CzechMap from "../components/CzechMap.svelte";
	import GlobalMap from "../components/GlobalMap.svelte";
	import { getTranslate } from "../utils/getTranslate";
	import translations from "./AboutUs.translations.json";
	import esgStrategyTranslations from "./EsgStrategy.translations.json";
	import MissionVisionValues from "../components/MissionVisionValues.svelte";
	import carFooterBanner from "../assets/images/car-footer-banner.png?webp&w=1920&imagetools";
	import ProductGroups from "../components/ProductGroups.svelte";
	import History from "../components/History.svelte";
	import paveljuricek from "../assets/images/paveljuricek.jpeg?webp&w=1920&imagetools";
	import familyVision from "../assets/images/familyvision.jpg?webp&h=582&imagetools";
	import Link from "../components/Link.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { getContext } from "svelte";
	import type { ProductGroupWithTextifiedContent } from "../../../core/schema/ProductGroup.js";
	import { productGroupsContextKey } from "../contexts/productGroupsContextKey.js";
	import type { ContactCardPerson } from "../components/ContactCardPerson.js";
	import branoArealView from "../assets/images/brano-areal-view-colored.png?webp&w=1920&imagetools";
	import paveljuricekCard from "../assets/images/management/juricek-pavel.jpg?webp&h=580&imagetools";
	import vaclavjuricekCard from "../assets/images/management/juricek-vaclav.jpg?webp&h=580&imagetools";
	import jozefhodorCard from "../assets/images/management/hodor-jozef.jpg?webp&h=580&imagetools";
	import alesaujedzkyCard from "../assets/images/management/aujezdsky-ales.jpg?webp&h=580&imagetools";
	import paveltaborskyCard from "../assets/images/management/taborsky-pavel.jpg?webp&h=580&imagetools";
	import petrskrobanekCard from "../assets/images/management/skrobanek-petr.jpg?webp&h=580&imagetools";
	import alenahahnovaCard from "../assets/images/management/hahnova-alena.jpg?webp&h=580&imagetools";
	import petrpetrCard from "../assets/images/management/petr-petr.jpg?webp&h=580&imagetools";
	import liborkudelaCard from "../assets/images/management/kudela-libor.jpg?webp&h=580&imagetools";
	import EsgStrategyCards from "../components/EsgStrategyCards.svelte";
	import { getPathInLocale } from "../../../core/schema/paths/getPathInLocale.js";
	import { esgStrategyPathMap } from "../../../core/schema/paths/esgStrategyPathMap.js";
	import { getTenant } from "../contexts/tenantContextKey.js";
	import Certifications from "../components/Certifications.svelte";
	import Awards from "../components/Awards.svelte";
	import ModernTechnologicalInfrastructure from "../components/ModernTechnologicalInfrastructure.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ContactCard from "../components/ContactCard.svelte";

	const { locale } = getTenant();

	const productGroups = getContext<ProductGroupWithTextifiedContent[]>(productGroupsContextKey);
	const translate = getTranslate(translations);
	const esgStrategyTranslate = getTranslate(esgStrategyTranslations);

	const managementPeople: ContactCardPerson[] = [
		{
			name: "Pavel JUŘÍČEK",
			description: translate("pavelJuricek"),
			image: paveljuricekCard,
		},
		{
			name: "Václav JUŘÍČEK",
			description: translate("vaclavJuricek"),
			image: vaclavjuricekCard,
		},
		{
			name: "Jozef HODOR",
			description: translate("jozefHodor"),
			image: jozefhodorCard,
		},
		{
			name: "Pavel TÁBORSKÝ",
			description: translate("pavelTaborsky"),
			image: paveltaborskyCard,
		},
		{
			name: "Petr ŠKROBÁNEK",
			description: translate("petrSkrobanek"),
			image: petrskrobanekCard,
		},
		{
			name: "Aleš AUJEZDSKÝ",
			description: translate("alesAujezdsky"),
			image: alesaujedzkyCard,
		},
		{
			name: "Petr PETR",
			description: translate("petrPetr"),
			image: petrpetrCard,
		},
		{
			name: "Libor KUDĚLA",
			description: translate("liborKudela"),
			image: liborkudelaCard,
		},
		{
			name: "Alena HAHNOVÁ",
			description: translate("alenaHahnova"),
			image: alenahahnovaCard,
		},
	];

	const heroTiles = [
		{
			headline: translate("established"),
			value: {
				cs: "1862",
				en: "1862",
			},
		},
		{
			headline: translate("employees"),
			value: {
				cs: "1600+",
				en: "1600+",
			},
		},
		{
			headline: translate("shopFloor"),
			value: {
				cs: "20+ ha",
				en: "20+ ha",
			},
		},
		{
			headline: translate("machines"),
			value: {
				cs: "520+",
				en: "520+",
			},
		},
		{
			headline: translate("turnover"),
			value: {
				cs: "300+ mil. eur",
				en: "€300+ mio",
			},
		},
		{
			headline: translate("manufacturingPlants"),
			value: {
				cs: "13",
				en: "13",
			},
		},
	];
</script>

<Layout footerImage={carFooterBanner} footerImageClass="max-lg:object-[80%_center]">
	<HeroBanner
		title={translate("aboutCompany", {}, true)}
		overlay="white-70"
		textColor="black"
		hideShowMore
		backgroundVideo={aboutHeader}
		backgroundVideoMobile={aboutHeaderMobile}
		videoPoster={aboutPoster}
		noLoopVideo
	>
		<div class="grid w-full grid-cols-2 gap-[0.44rem] lg:grid-cols-3 lg:gap-[1.29rem]">
			{#each heroTiles as tile}
				<div
					class="group relative flex w-full flex-col justify-between px-[1.24rem] py-[0.77rem] backdrop-blur lg:px-[3.625rem]"
				>
					<span class="text-[1.125rem] leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52]">
						{tile.headline}
					</span>
					<span class="text-[1.75rem] font-bold leading-[1.21] lg:text-[2.375rem] lg:leading-[1.31]">
						{nbspify(tile.value[locale])}
					</span>
					<div
						class="absolute inset-0 -z-10 bg-white opacity-50 transition-colors duration-500 group-hover:bg-white/10"
					></div>
				</div>
			{/each}
		</div>
	</HeroBanner>

	<CzechMap />
	<GlobalMap />

	<section class="container flex min-h-screen flex-col justify-between gap-8 py-12 lg:flex-row lg:gap-12">
		<div class="flex flex-col gap-14 lg:w-[51rem] lg:gap-20">
			<h2 class="m-0 text-[2.75rem] font-semibold lg:text-4xl">{translate("proudRoots")}</h2>
			<p class="lg:text-2lg text-[2rem]">{translate("proudRootsText")}</p>
		</div>
		<div class="flex-col gap-[3rem] lg:flex lg:w-[28rem]">
			<div>
				<h3 class="mb-12 text-[1.125rem] font-bold lg:text-[1.1875rem]">{translate("localHeart")}</h3>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">{translate("localHeartText")}</p>
			</div>
			<div>
				<h3 class="mb-12 text-[1.125rem] font-bold lg:text-[1.1875rem]">{translate("extensiveIntegration")}</h3>
				<p class="text-[1.125rem] font-light lg:text-[1.1875rem]">{translate("extensiveIntegrationText")}</p>
			</div>
		</div>
	</section>

	<section class="relative flex min-h-screen px-5 pb-16 pt-16 lg:px-16 lg:pb-44 lg:pt-48">
		<div
			class="absolute inset-0 bg-[linear-gradient(204deg,var(--tw-gradient-stops))] from-[#73737317] to-black/45"
		></div>
		<div class="absolute inset-0 bg-gradient-to-t from-transparent to-black/20 lg:hidden"></div>

		<StaticPicture
			image={paveljuricek}
			width={1920}
			height={1080}
			imgClass="absolute inset-0 h-full -z-10 w-full object-cover"
			alt="Pavel Juříček"
		/>

		<div class="container z-10 flex flex-1 flex-col items-stretch justify-between gap-80 lg:gap-12">
			<div class="flex flex-col">
				<span
					class="text-secondary w-10/12 text-xl font-semibold leading-[1.136] lg:w-2/3 lg:text-4xl lg:leading-[5.25rem]"
				>
					Pavel Juříček
				</span>
				<span class="lg:text-2md text1-[1.125rem] text-secondary font-bold">{translate("owner")}</span>
			</div>

			<div class="text-secondary flex flex-1 grid-cols-2 flex-col justify-between lg:grid">
				<div class="flex flex-col justify-between lg:w-10/12">
					<p class="text-[2rem] leading-tight lg:text-[2.625rem]">{translate("pursuit")}</p>
					<p class="text-[1.125rem] lg:w-2/3 lg:text-[1.1875rem] lg:font-bold">{translate("cooperation")}</p>
				</div>
				<div class="ml-auto flex flex-col justify-end text-[1.125rem] lg:mr-32 lg:w-1/2 lg:text-[1.1875rem]">
					<p class="font-bold">{translate("continuousImprovementHeader")}</p>
					<p class="font-light">{translate("continuousImprovementText")}</p>
				</div>
			</div>
		</div>
	</section>

	<section class="relative -top-16 min-h-screen">
		<div class="container flex flex-col gap-8 lg:gap-32">
			<div class="flex grid-cols-12 grid-rows-[auto,auto] flex-col lg:grid">
				<div
					class="bg-tertiary/90 col-span-full flex w-full grid-cols-subgrid grid-rows-[auto,5rem] flex-col gap-8 gap-y-2 backdrop-blur lg:grid xl:grid-rows-2"
				>
					<div
						class="col-span-full flex grid-cols-subgrid flex-col px-6 py-16 pb-4 md:pb-16 lg:grid lg:items-baseline lg:px-14 lg:pb-0 xl:pt-32"
					>
						<span class="col-span-5 mb-4 max-w-[26.5rem] text-[2.75rem] font-semibold leading-[1.13] lg:text-4xl">
							{translate("familyVision")}
						</span>
						<span class="lg:text-2lg col-span-7 max-w-[35.625rem] text-[2rem] leading-[1.13]"
							>{translate("fromGeneration")}</span
						>
					</div>
					<div class="relative lg:col-span-7 lg:col-start-6">
						<StaticPicture
							class="left-0 right-0 lg:absolute"
							image={familyVision}
							imgClass="w-full"
							width={1000}
							height={582}
							alt={translate("familyVision")}
						/>
					</div>
				</div>

				<div class="col-span-4 flex flex-col gap-4 pt-9 text-[1.125rem] lg:p-14 lg:pt-32 lg:text-[1.1875rem]">
					<span class="font-bold">{translate("inheritanceHeadline")}</span>
					<span class="font-light">{translate("inheritancePerex")}</span>
				</div>
			</div>
		</div>
	</section>

	<MissionVisionValues />

	<section class="container min-h-screen py-12">
		<History />
	</section>

	<section class="container min-h-screen">
		<h2 class="mb-8 text-[2.75rem] font-semibold lg:text-4xl">{translate("management")}</h2>
		<p class="lg:text-2lg mb-32 text-[2rem]">{translate("managementText")}</p>

		<div class="grid grid-cols-1 md:grid-cols-2 md:gap-5 lg:grid-cols-3">
			{#each managementPeople as person, index}
				<ContactCard {index} {person} />
			{/each}
		</div>
	</section>

	<section>
		<div class="flex flex-col">
			<div class="container flex gap-8 px-5 py-28 lg:px-12 lg:pb-48 lg:pt-80">
				<div class="flex flex-col gap-[inherit]">
					<h2 class="m-0 text-[2.75rem] font-semibold lg:text-4xl">{esgStrategyTranslate("headline")}</h2>
					<p class="lg:text-2lg text-[2rem]">{esgStrategyTranslate("sustainableFuturePerex")}</p>
				</div>
				<Link asButton href="/{getPathInLocale(locale, esgStrategyPathMap)}" class="ml-auto hidden lg:flex">
					{translate("exploreMore")}
				</Link>
			</div>

			<EsgStrategyCards>
				<div class="relative mt-10 pb-48 lg:hidden" slot="below-cards">
					<StaticPicture
						image={branoArealView}
						class="absolute bottom-0 left-0 right-0 -z-10"
						imgClass="object-cover !w-full max-h-full"
						width={1324}
						height={349}
						alt=""
					/>
					<Link asButton href="/{getPathInLocale(locale, esgStrategyPathMap)}" containerClass="container !w-fit">
						{translate("exploreMore")}
					</Link>
				</div>
			</EsgStrategyCards>
		</div>
	</section>

	<ModernTechnologicalInfrastructure />

	<Certifications />

	<Awards />

	<section class="mt-[5rem] lg:mt-[15rem]">
		<div class="border-tertiary-300 container border-b py-8">
			<span class="text-[2rem] font-normal leading-[1.18] lg:text-[2.375rem]">{translate("whereElse")}</span>
			<h3 class="text-lg font-semibold leading-[3.125rem] sm:text-[2rem] lg:text-[4.625rem] lg:leading-[5.25rem]">
				{translate("exploreOurPortfolio")}
			</h3>
		</div>
		<div class="container">
			<ProductGroups {productGroups} topOffset={false} />
		</div>
	</section>
</Layout>
